import { useEffect, useMemo } from "react";
import { Circle, CircleMarker, MapContainer, TileLayer, useMap } from "react-leaflet";
import { latLng } from "leaflet";
import { MapPin } from "lucide-react";

function toCoordinate(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

function DeliveryRadiusViewport({ latitude, longitude, radiusMeters }) {
  const map = useMap();

  useEffect(() => {
    const center = [latitude, longitude];

    if (radiusMeters <= 0) {
      map.setView(center, 14, { animate: false });
      return;
    }

    const bounds = latLng(latitude, longitude).toBounds(radiusMeters);
    map.fitBounds(bounds, { animate: false, padding: [24, 24], maxZoom: 16 });
  }, [latitude, longitude, map, radiusMeters]);

  return null;
}

export default function DeliveryRadiusMap({ latitude, longitude, maxDeliveryKm }) {
  const validLatitude = toCoordinate(latitude);
  const validLongitude = toCoordinate(longitude);
  const hasValidCoordinates = (
    validLatitude !== null
    && validLongitude !== null
    && validLatitude >= -90
    && validLatitude <= 90
    && validLongitude >= -180
    && validLongitude <= 180
  );
  const radiusMeters = useMemo(() => {
    const radiusKm = Number(maxDeliveryKm);
    return Number.isFinite(radiusKm) && radiusKm > 0 ? radiusKm * 1000 : 0;
  }, [maxDeliveryKm]);

  if (!hasValidCoordinates) {
    return (
      <div
        className={
          "grid h-48 place-items-center rounded-2xl border border-dashed border-gray-200 "
          + "bg-gray-50 px-4 text-center text-xs text-gray-500"
        }
        data-testid="branch-delivery-radius-map-empty"
      >
        <div className="flex flex-col items-center gap-2">
          <MapPin size={20} className="text-gray-400" />
          <span>لا تتوفر إحداثيات للفرع لعرض نطاق التوصيل.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-none h-48 overflow-hidden rounded-2xl border border-gray-200"
      data-testid="branch-delivery-radius-map"
      data-latitude={validLatitude}
      data-longitude={validLongitude}
      data-radius-meters={radiusMeters}
      aria-label="خريطة نطاق التوصيل"
    >
      <MapContainer
        center={[validLatitude, validLongitude]}
        zoom={14}
        className="h-full w-full"
        dragging={false}
        touchZoom={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        boxZoom={false}
        keyboard={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <DeliveryRadiusViewport
          latitude={validLatitude}
          longitude={validLongitude}
          radiusMeters={radiusMeters}
        />
        {radiusMeters > 0 && (
          <Circle
            center={[validLatitude, validLongitude]}
            radius={radiusMeters}
            pathOptions={{
              color: "#16a34a",
              fillColor: "#22c55e",
              fillOpacity: 0.22,
              weight: 2,
            }}
            interactive={false}
          />
        )}
        <CircleMarker
          center={[validLatitude, validLongitude]}
          radius={9}
          pathOptions={{
            color: "#ffffff",
            fillColor: "#f97316",
            fillOpacity: 1,
            weight: 3,
          }}
          interactive={false}
          data-testid="branch-delivery-radius-map-marker"
        />
      </MapContainer>
    </div>
  );
}